import { Add, Edit } from "@mui/icons-material";
import { Button, Grid } from "@mui/material";
import { useState } from "react";
import { useDialog } from "../../common/dialog";
import { createListStore } from "../../common/listStore";
import { listHeaderItems, renderListStore } from "../../common/listStoreUi";
import { PageTitle } from "../../layout/PageTitle";
import { InventoryTransactionDialog } from "./InventoryTransactionDialog";
import { useArticlesWithCode, useTransactionCodes, useWarehouses } from "../../common/enum";
import { createEntityStore } from "../../common/entityStore";
import { propDateField, propEnumField, propNumberField } from "../../common/entityStoreForm";
import { applyListStoreFilters } from "../../common/listStoreFilters";

export function InventoryTransactionListPage() {
    const [{ useStore, useFiltersStore }] = useState(() => {
        const useStore = createListStore<InventoryTransactionListItemDto>({
            path: "inventoryTransactions/list",
            supportsApiFilterSortPage: true,
            itemsPerPage: 15,
            getId: p => p.id,
            listProps: [
                { column: "transactionCodeId", label: "Code", width: 150, enumStore: useTransactionCodes },
                { column: "date", label: "Date", width: 100, type: "date" },
                { column: "inventoryArticleId", label: "Article", width: 350, enumStore: useArticlesWithCode },
                { column: "fromWarehouseId", label: "From warehouse", width: 120, enumStore: useWarehouses },
                { column: "toWarehouseId", label: "To warehouse", width: 120, enumStore: useWarehouses },
                { column: "qty", label: "Quantity", width: 120, type: "number" }
            ],
        });
        const useFiltersStore = createEntityStore<ListFilters>({ data: {} });
        applyListStoreFilters(useFiltersStore, useStore, [
            { listProps: ["date"], operator: "GreaterThanOrEqual", filterProp: "dateFrom" },
            { listProps: ["date"], operator: "LessThanOrEqual", filterProp: "dateTo" },
            { listProps: ["qty"], operator: "GreaterThanOrEqual", filterProp: "qtyFrom" },
            { listProps: ["qty"], operator: "LessThanOrEqual", filterProp: "qtyTo" },
            { listProps: ["fromWarehouseId"], operator: "Equal", filterProp: "fromWarehouseId" },
            { listProps: ["toWarehouseId"], operator: "Equal", filterProp: "toWarehouseId" },
            { listProps: ["transactionCodeId"], operator: "Equal", filterProp: "transactionCodeId" },
            { listProps: ["inventoryArticleId"], operator: "Equal", filterProp: "inventoryArticleId" },
        ]);
        return { useStore, useFiltersStore };
    });

    const inventoryTransactions = useStore(p => ({ selectedItem: p.selectedItem }));
    const newDialog = useDialog(close => <InventoryTransactionDialog close={close} inventoryTransaction={null}
        onChange={() => { useStore.getState().fetch(); }} />, "xs");
    const editDialog = useDialog(close => <InventoryTransactionDialog close={close} inventoryTransaction={inventoryTransactions.selectedItem!}
        onChange={() => { useStore.getState().fetch(); }} />, "xs");

    return <>
        {newDialog.render()}
        {editDialog.render()}
        <PageTitle title="Inventory transactions" />
        {renderListStore(useStore, {
            onDoubleClick: () => {
                editDialog.show();
            },
            header: listHeaderItems([
                ["edit", () => <Button color="edit" disabled={inventoryTransactions.selectedItem == null} title="Edit" onClick={() => editDialog.show()}><Edit /></Button>],
                ["add", () => <Button color="add" title="Add" onClick={() => newDialog.show()}><Add /></Button>],
            ]),
            renderExternalFilters: () => <>
                <Grid item xs={1}>{propEnumField(useFiltersStore)("transactionCodeId", "Code", useTransactionCodes, { addAll: true })}</Grid>
                <Grid item xs={1}>{propDateField(useFiltersStore)("dateFrom", "dateFromFormatted", "Date from")}</Grid>
                <Grid item xs={1}>{propDateField(useFiltersStore)("dateTo", "dateToFormatted", "Date to")}</Grid>
                <Grid item xs={2}>{propEnumField(useFiltersStore)("inventoryArticleId", "Article", useArticlesWithCode, { addAll: true })}</Grid>
                <Grid item xs={1}>{propEnumField(useFiltersStore)("fromWarehouseId", "From warehouse", useWarehouses, { addAll: true })}</Grid>
                <Grid item xs={1}>{propEnumField(useFiltersStore)("toWarehouseId", "To warehouse", useWarehouses, { addAll: true })}</Grid>
                <Grid item xs={1}>{propNumberField(useFiltersStore)("qtyFrom", "qtyFromFormatted", "Quantity from")}</Grid>
                <Grid item xs={1}>{propNumberField(useFiltersStore)("qtyTo", "qtyToFormatted", "Quantity to")}</Grid>
            </>,
            clearExternalFilters: () => useFiltersStore.getState().setClientData({}),
        })}
    </>;
}

interface InventoryTransactionListItemDto {
    id: number;
    date: string;
    qty: number;
    inventoryArticleId: number;
    transactionCodeId: number;
    fromWarehouseId: number | null;
    toWarehouseId: number | null;
}

interface ListFilters {
    dateFrom?: string;
    dateTo?: string;
    dateFromFormatted?: string;
    dateToFormatted?: string;
    qtyFrom?: number;
    qtyTo?: number;
    qtyFromFormatted?: string;
    qtyToFormatted?: string;
    inventoryArticleId?: number;
    transactionCodeId?: number;
    fromWarehouseId?: number;
    toWarehouseId?: number;
}
